import React, { useState, useEffect } from 'react';
import { Shield, Mail, Key, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { setAccessToken } from '../utils/api';
import { useI18n } from '../i18n';
import { interpolate } from '../locales/superAdmin';

/**
 * SuperAdminAuth - Two-factor authentication for SuperAdmin access
 * 
 * Flow:
 * 1. SuperAdmin logs in with magic link (normal login)
 * 2. When accessing SuperAdmin features, this component shows
 * 3. User requests confirmation code (sent to email)
 * 4. User enters code and gets elevated access (JWT)
 */
const SuperAdminAuth = ({ user, onAuthenticated, onCancel }) => {
    const { t } = useI18n(user?.club?.language);
    const sa = t.superAdmin;
    const [step, setStep] = useState('request'); // 'request' | 'verify' | 'success'
    const [email, setEmail] = useState(user?.email || '');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [countdown, setCountdown] = useState(0);

    // Countdown for resend button
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Auto-focus on code input
    useEffect(() => {
        if (step === 'verify') {
            const input = document.querySelector('.code-input');
            if (input) input.focus();
        }
    }, [step]);

    const requestCode = async () => {
        if (!email) {
            setError(sa.auth.emailRequired);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await fetch('/api/superadmin/auth/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            const data = await res.json();

            if (res.ok) {
                setMessage(sa.auth.codeSent);
                setStep('verify');
                setCountdown(60); // Can resend after 60 seconds
            } else {
                setError(data.error || sa.auth.codeRequestError);
            }
        } catch (err) {
            setError(sa.common.networkErrorRetry);
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async () => {
        if (!confirmationCode || confirmationCode.length < 6) {
            setError(sa.auth.codeRequired);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await fetch('/api/superadmin/auth/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    email, 
                    confirmationKey: confirmationCode 
                })
            });

            const data = await res.json();

            if (res.ok) {
                setStep('success');
                // Store the SuperAdmin token using the standard API utility
                // This ensures apiFetch will include it in Authorization header
                setAccessToken(data.accessToken);
                localStorage.setItem('masaklubi_superadmin_verified', 'true');
                
                // Notify parent after brief animation
                setTimeout(() => {
                    onAuthenticated(data);
                }, 1000);
            } else {
                setError(data.error || sa.auth.invalidCode);
                // Clear the input on error
                setConfirmationCode('');
            }
        } catch (err) {
            setError(sa.common.networkErrorRetry);
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setConfirmationCode(value);
        
        // Auto-submit when 6 digits entered
        if (value.length === 6) {
            setTimeout(() => verifyCode(), 100);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (step === 'request') {
                requestCode();
            } else if (step === 'verify') {
                verifyCode();
            }
        }
    };

    return (
        <div className="superadmin-auth-overlay">
            <div className="superadmin-auth-modal">
                {/* Header */}
                <div className="auth-header">
                    <Shield size={32} className="shield-icon" />
                    <h2>{sa.auth.title}</h2>
                    <p>{sa.auth.subtitle}</p>
                </div>

                {/* Step: Request Code */}
                {step === 'request' && (
                    <div className="auth-step">
                        <div className="step-description">
                            <Mail size={20} />
                            <span>{sa.auth.requestDescription}</span>
                        </div>

                        <div className="input-group">
                            <label>{sa.auth.email}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={sa.auth.emailPlaceholder}
                                disabled={loading}
                            />
                        </div>

                        {error && (
                            <div className="auth-error">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <div className="auth-actions">
                            <button 
                                className="cancel-btn"
                                onClick={onCancel}
                                disabled={loading}
                            >
                                {sa.common.cancel}
                            </button>
                            <button 
                                className="primary-btn"
                                onClick={requestCode}
                                disabled={loading || !email}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="spin" />
                                        {sa.common.sending}
                                    </>
                                ) : (
                                    <>
                                        <Mail size={18} />
                                        {sa.auth.sendCode}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step: Verify Code */}
                {step === 'verify' && (
                    <div className="auth-step">
                        <div className="step-description success">
                            <CheckCircle size={20} />
                            <span>{message}</span>
                        </div>

                        <div className="input-group">
                            <label>
                                <Key size={16} />
                                {sa.auth.codeLabel}
                            </label>
                            <input
                                type="text"
                                className="code-input"
                                value={confirmationCode}
                                onChange={handleCodeChange}
                                onKeyDown={handleKeyDown}
                                placeholder={sa.auth.codePlaceholder}
                                maxLength={6}
                                disabled={loading}
                                autoComplete="one-time-code"
                            />
                            <span className="input-hint">
                                {sa.auth.codeHint}
                            </span>
                        </div>

                        {error && (
                            <div className="auth-error">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        <div className="auth-actions">
                            <button 
                                className="cancel-btn"
                                onClick={() => {
                                    setStep('request');
                                    setConfirmationCode('');
                                    setError('');
                                }}
                                disabled={loading}
                            >
                                {sa.common.back}
                            </button>
                            <button 
                                className="primary-btn"
                                onClick={verifyCode}
                                disabled={loading || confirmationCode.length < 6}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="spin" />
                                        {sa.common.verifying}
                                    </>
                                ) : (
                                    <>
                                        <Shield size={18} />
                                        {sa.common.verify}
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Resend link */}
                        <div className="resend-section">
                            {countdown > 0 ? (
                                <span className="resend-countdown">
                                    {interpolate(sa.auth.resendCountdown, { count: countdown })}
                                </span>
                            ) : (
                                <button 
                                    className="resend-btn"
                                    onClick={() => {
                                        setStep('request');
                                        setConfirmationCode('');
                                        setError('');
                                    }}
                                >
                                    {sa.auth.resendPrompt}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Step: Success */}
                {step === 'success' && (
                    <div className="auth-step success-step">
                        <div className="success-icon">
                            <CheckCircle size={48} />
                        </div>
                        <h3>{sa.auth.successTitle}</h3>
                        <p>{sa.auth.successRedirect}</p>
                        <Loader2 size={24} className="spin" />
                    </div>
                )}
            </div>

            <style>{`
                .superadmin-auth-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.8);
                    backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .superadmin-auth-modal {
                    background: linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%);
                    border: 1px solid rgba(139, 92, 246, 0.3);
                    border-radius: 16px;
                    padding: 32px;
                    max-width: 420px;
                    width: 90%;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                }

                .auth-header {
                    text-align: center;
                    margin-bottom: 28px;
                }

                .auth-header .shield-icon {
                    color: #8b5cf6;
                    margin-bottom: 12px;
                }

                .auth-header h2 {
                    margin: 0;
                    color: #fff;
                    font-size: 1.5rem;
                }

                .auth-header p {
                    margin: 8px 0 0;
                    color: #a0a0b0;
                    font-size: 0.9rem;
                }

                .auth-step {
                    animation: slideUp 0.3s ease;
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .step-description {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: rgba(139, 92, 246, 0.1);
                    border-radius: 8px;
                    color: #c0c0d0;
                    font-size: 0.9rem;
                    margin-bottom: 20px;
                }

                .step-description.success {
                    background: rgba(34, 197, 94, 0.1);
                    color: #22c55e;
                }

                .input-group {
                    margin-bottom: 20px;
                }

                .input-group label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #a0a0b0;
                    font-size: 0.85rem;
                    margin-bottom: 8px;
                }

                .input-group input {
                    width: 100%;
                    padding: 14px 16px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    color: #fff;
                    font-size: 1rem;
                    transition: all 0.2s;
                }

                .input-group input:focus {
                    outline: none;
                    border-color: #8b5cf6;
                    background: rgba(139, 92, 246, 0.1);
                }

                .input-group input.code-input {
                    text-align: center;
                    font-size: 1.8rem;
                    letter-spacing: 8px;
                    font-family: 'Monaco', 'Consolas', monospace;
                }

                .input-hint {
                    display: block;
                    margin-top: 8px;
                    color: #707080;
                    font-size: 0.8rem;
                }

                .auth-error {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    border-radius: 8px;
                    color: #ef4444;
                    font-size: 0.9rem;
                    margin-bottom: 20px;
                }

                .auth-actions {
                    display: flex;
                    gap: 12px;
                }

                .auth-actions button {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 14px 20px;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: none;
                }

                .cancel-btn {
                    background: rgba(255, 255, 255, 0.05);
                    color: #a0a0b0;
                }

                .cancel-btn:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.1);
                }

                .primary-btn {
                    background: linear-gradient(135deg, #8b5cf6, #6366f1);
                    color: white;
                }

                .primary-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
                }

                .primary-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .resend-section {
                    margin-top: 20px;
                    text-align: center;
                }

                .resend-countdown {
                    color: #707080;
                    font-size: 0.85rem;
                }

                .resend-btn {
                    background: none;
                    border: none;
                    color: #8b5cf6;
                    cursor: pointer;
                    font-size: 0.9rem;
                }

                .resend-btn:hover {
                    text-decoration: underline;
                }

                .success-step {
                    text-align: center;
                    padding: 20px 0;
                }

                .success-icon {
                    color: #22c55e;
                    margin-bottom: 16px;
                }

                .success-step h3 {
                    margin: 0;
                    color: #fff;
                }

                .success-step p {
                    color: #a0a0b0;
                    margin: 8px 0 20px;
                }

                .spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default SuperAdminAuth;

